'use client';

import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_EDITOR_CHROME,
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_VALUE_TEXT
} from '@/components/attributes';
import { cn } from '@/lib/utils';

/**
 * **"Create it inline" as a second step inside the search popover** (LF-6314).
 *
 * The three inventory search fields — institutions, HIN tickers, SRN tickers —
 * each let a user mint an entity the directory does not have yet. All three used
 * to do it the same way: close the popover, and reveal a bordered card *below* the
 * field with a heading, a description, two `Label` + `Input` pairs and a button
 * row. Two things were wrong with that, and they compound:
 *
 * · **It threw the reader out of the surface they were working in.** "Search, and
 *   if it is not there, create it" is one action in two parts; closing the popover
 *   and drawing a panel somewhere else made it read as two unrelated ones.
 * · **The panel was chrome from a different design language than the field.** A
 *   muted card with its own heading, sitting under a control that had just
 *   vanished.
 *
 * So the popover advances to this step and comes back from it, and the step is
 * built from the **attribute treatment's own tokens**
 * (`components/attributes/attribute-tokens.ts`, promoted to production by
 * [ATTR-3] and moved to its shared home by [ATTR-10]): the label is
 * `ATTRIBUTE_LABEL_TEXT` in `ATTRIBUTE_MUTED_INK`, the value `ATTRIBUTE_VALUE_TEXT`,
 * and each input wears `ATTRIBUTE_EDITOR_CHROME` — the same on-box edge an open
 * attribute editor wears. It reads as one surface with the list it replaced.
 *
 * ## Not attribute *rows*, and that is deliberate
 *
 * A row commits on blur into a record that already exists. This is a create form
 * for an entity that does not exist yet, where every field is required before
 * anything can be committed at all. Borrowing the treatment's rows here would mean
 * either committing half an institution or bolting a submit button onto a system
 * whose entire argument is that it does not need one. **The tokens are the
 * reusable part; the interaction contract is not.**
 *
 * ## One component, three callers
 *
 * Extracted rather than written per picker: the three panels were already
 * identical apart from their field labels, and the version before this one had
 * started a fourth copy inside `InstitutionSearchField`. A shared step is also the
 * only way the three stay consistent the next time one of them is touched.
 */
export interface InlineCreateField {
  label: string;
  value: string;
  placeholder: string;
  type?: 'text' | 'email';
  /** The first field of the step takes focus, so it is typeable immediately. */
  autoFocus?: boolean;
  onChange: (value: string) => void;
}

export interface InlineCreateSelectOption {
  value: string;
  label: string;
  /**
   * What picking this option means, in the reader's terms — rendered by
   * `SelectItem`'s own `description` slot as muted subtext under the label.
   * Radix mirrors only `ItemText` into the collapsed trigger, so this shows in
   * the open list and never crowds the resting control.
   */
  description?: string;
}

/**
 * A single-choice field on the step, styled to sit with the text inputs.
 *
 * Separate from {@link InlineCreateField} rather than a `kind` on it, because the
 * two have opposite completeness contracts: a text field starts empty and the
 * step's `ready` gate waits for it, while a select always carries a value (its
 * caller supplies the default) and so never gates submission. Folding them into
 * one shape would mean teaching `ready` to special-case a member of `fields`.
 *
 * Exported so the fetching wrapper that supplies the options can render it — see
 * `renderSelects` on {@link InlineCreateStep} for why the options do not simply
 * arrive as a prop.
 */
export function InlineCreateSelectField({
  label,
  value,
  options,
  onChange,
  hint
}: {
  label: string;
  value: string;
  options: InlineCreateSelectOption[];
  onChange: (value: string) => void;
  /** Muted line under the control, for what the choice affects downstream. */
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span
        className={cn(
          ATTRIBUTE_LABEL_TEXT,
          'text-foreground-secondary font-medium'
        )}
      >
        {label}
      </span>
      <Select value={value} onValueChange={onChange}>
        {/*
          Matches the text inputs' own chrome and height exactly
          (`ATTRIBUTE_EDITOR_CHROME` + `py-2.5`), for the reason the inputs carry
          that measured height in the first place: a control that changes size
          between steps moves under the pointer.
        */}
        <SelectTrigger
          className={cn(
            ATTRIBUTE_EDITOR_CHROME,
            ATTRIBUTE_VALUE_TEXT,
            ATTRIBUTE_LEADING,
            'h-auto w-full border-0 px-1.5 py-2.5 shadow-none focus:ring-0'
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              description={option.description}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint ? (
        <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>{hint}</p>
      ) : null}
    </div>
  );
}

export function InlineCreateStep({
  title,
  fields,
  renderSelects,
  hint,
  submitLabel,
  onCancel,
  onSubmit
}: {
  /** Names what is being created — `New bank`, `New ticker`. */
  title: string;
  fields: InlineCreateField[];
  /**
   * Optional single-choice fields, rendered below `fields` (VALE-1577).
   *
   * **A render prop, not a value, and that is load-bearing.** The only caller with
   * a choice to offer has to fetch its options, and a hook cannot be called
   * conditionally — so fetching in the parent would run for all ~20 pickers that
   * mount this step, including in the many tests that render them with no
   * `QueryClientProvider`, where React Query throws outright. Handing back a node
   * keeps the fetch inside a child that only mounts on the branch that needs it.
   *
   * **Optional on purpose** besides: this step is shared by four callers — the
   * institution create, the institution edit, and the HIN/SRN ticker pickers
   * — and omitted, every one of them renders byte-identically to before.
   */
  renderSelects?: () => ReactNode;
  /** One line on why the fields are required. Replaces the old card's paragraph. */
  hint?: string;
  submitLabel: string;
  /** Returns to the search step. NOT a popover dismiss — see the header. */
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const ready = fields.every((field) => field.value.trim().length > 0);

  const submitIfReady = () => {
    if (ready) onSubmit();
  };

  return (
    <div className="flex flex-col">
      {/*
        A step header with a back affordance, so the popover reads as having gone
        somewhere it can come back from rather than as having been replaced. Cancel
        returns to the list rather than dismissing the popover, which is the
        difference between a step and a detour.
      */}
      <div className="border-border-subtle flex items-center gap-1 border-b px-2 py-1.5">
        <Button
          tone="muted"
          pad="xs"
          weight="normal"
          type="button"
          variant="ghost"
          size="2xs"
          onClick={onCancel}
        >
          <ChevronLeft className="h-3.5 w-3.5 shrink-0" />
          Back
        </Button>
        {/*
          The title sits at the FAR end of the header row, not beside Back
          (VALE-1577). Adjacent, the two read as one cluster — "‹ Back New
          organisation" — where the step's name looks like part of the control
          that leaves it. Pushed to the opposite edge, Back reads as navigation
          and the title as the step's own name, which is what it is.
        */}
        <span
          className={cn(
            ATTRIBUTE_LABEL_TEXT,
            'text-foreground-secondary ml-auto pr-1 font-medium'
          )}
        >
          {title}
        </span>
      </div>

      <div className="flex flex-col gap-3 px-3 pt-3 pb-4">
        {/*
          The requirement line sits ABOVE the fields, not under them. Below the
          last input it read as that input's own helper text rather than as the
          step's — and it is more use before you start filling than after.
        */}
        {hint ? (
          <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
            {hint}
          </p>
        ) : null}
        {/*
          Fields in their own group at `gap-4`, against the `gap-1` between a label
          and its own input. The first pass had both at ~9–12px, so a label bound
          to its field no more strongly than to the field above it — the classic
          proximity failure, and the reason the step read as a list of six things
          rather than two.
        */}
        <div className="flex flex-col gap-4">
          {fields.map((field) => (
            <label key={field.label} className="flex flex-col gap-1">
              {/*
              `font-medium` so the label separates from the hint below, which
              shares its size — without it the two read as the same tier.
            */}
              <span
                className={cn(
                  ATTRIBUTE_LABEL_TEXT,
                  'text-foreground-secondary font-medium'
                )}
              >
                {field.label}
              </span>
              <div className={cn(ATTRIBUTE_EDITOR_CHROME, 'px-1.5')}>
                <Input
                  autoFocus={field.autoFocus}
                  type={field.type ?? 'text'}
                  value={field.value}
                  placeholder={field.placeholder}
                  onChange={(event) => field.onChange(event.target.value)}
                  /*
                  `Enter` submits from any field. The step is two inputs and a
                  button, and reaching for the mouse to finish a two-field form is
                  the friction this whole change is about.
                */
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      submitIfReady();
                    }
                  }}
                  /*
                  `py-2.5`, not the treatment's own `py-1`, and measured rather
                  than guessed: at `py-1` these were 24px against the search step's
                  37px input, so flipping between the steps changed the control size
                  under the pointer. Two intermediate values still read as smaller
                  to a reviewer (27px, then 31px), so this matches outright.
                */
                  className={cn(
                    ATTRIBUTE_VALUE_TEXT,
                    ATTRIBUTE_LEADING,
                    'h-auto py-2.5',
                    ATTRIBUTE_EDITOR_BARE
                  )}
                />
              </div>
            </label>
          ))}

          {/*
            Rendered inside the text fields' group, so a label/control pair here
            sits on the same `gap-4` rhythm as the ones above it rather than
            reading as a separate section bolted underneath.
          */}
          {renderSelects?.()}
        </div>

        <div className="flex justify-end">
          <Button
            size="xs"
            density="compact"
            type="button"
            disabled={!ready}
            onClick={onSubmit}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
